import { motion, useReducedMotion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { ASSETS } from "@/content/site";
import ContextLine from "@/components/ContextLine";

export default function Hero() {
  const navigate = useNavigate();
  const reduced = useReducedMotion();

  return (
    <section data-testid="hero-section" className="relative w-full overflow-hidden" style={{ height: "100svh", minHeight: "660px" }}>
      {/* Full-bleed lobby backdrop (branded imagery from the client) */}
      <motion.picture
        className="absolute inset-0"
        initial={reduced ? false : { scale: 1.04 }}
        animate={reduced ? undefined : { scale: 1 }}
        transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
      >
        <source srcSet={ASSETS.heroLobby} type="image/webp" />
        <img
          src="/assets/hero-siaq-lobby.jpg"
          alt="ردهة سياق: خط السياق يمتد فوق مدينة الرياض وشعار سياق على أرضية المكتب"
          width="2000"
          height="1116"
          fetchPriority="high"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
      </motion.picture>

      {/* Darkening filter over the whole image */}
      <div className="absolute inset-0 pointer-events-none" style={{ background: "rgba(15, 26, 42, 0.72)" }} />

      {/* Legibility gradients: strong on the copy side (right in RTL), light elsewhere */}
      <div className="absolute inset-0 pointer-events-none hidden lg:block" style={{ background: "linear-gradient(270deg, rgba(22,38,54,.78) 0%, rgba(22,38,54,.50) 34%, rgba(22,38,54,.12) 62%, transparent 100%)" }} />
      <div className="absolute inset-0 pointer-events-none lg:hidden" style={{ background: "linear-gradient(to top, rgba(22,38,54,.94) 0%, rgba(22,38,54,.65) 42%, rgba(22,38,54,.25) 74%, rgba(22,38,54,.08) 100%)" }} />

      <div className="relative h-full mx-auto max-w-[1400px] px-6 sm:px-10">
        <motion.div dir="rtl" className="absolute z-20 text-right text-white max-w-[39rem] left-6 right-6 bottom-12 sm:left-10 sm:right-10 lg:right-10 lg:left-auto lg:bottom-auto lg:top-1/2 lg:-translate-y-1/2 lg:bg-navy/35 lg:p-7 lg:border-r-2 lg:border-coral"
          initial={reduced ? false : { opacity:0, y:20 }} animate={reduced ? undefined : { opacity:1, y:0 }} transition={{duration:.8,delay:.15}}>
          <span className="eyebrow mb-5" data-testid="hero-eyebrow">سياق للاستشارات وحلول تمكين القدرات</span>
          <h1 data-testid="hero-headline" className="font-display leading-[1.15]" style={{fontSize:"clamp(2.2rem,4.6vw,4.6rem)"}}>لسياقك نبتكر</h1>
          <p data-testid="hero-subtext" className="mt-5 text-white/90 text-base sm:text-lg leading-loose">
            نبتكر في سياق حلولًا استشارية مخصصة تنطلق من فهم عميق للاحتياج، وتمكّن المنظمات من تحقيق أثر مجتمعي مستدام عبر منهجيات مهنية مبتكرة.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <button onClick={() => navigate("/discover-context")} data-testid="hero-primary-cta" className="group inline-flex items-center gap-2 bg-coral text-white px-7 py-4 font-semibold">
              ابدأ تشخيص منظمتك <ArrowLeft size={18} className="transition-transform group-hover:-translate-x-1" />
            </button>
            <button onClick={() => navigate("/about")} className="inline-flex items-center gap-2 text-white border border-white/45 px-7 py-4 font-medium hover:bg-white hover:text-navy transition-colors">عن سياق</button>
          </div>
        </motion.div>
      </div>
      <ContextLine d="M40,20 C300,90 520,10 760,70 C980,120 1120,40 1200,80" viewBox="0 0 1200 120" className="absolute bottom-0 inset-x-0 h-20 w-full z-10" strokeWidth={2.5} />
    </section>
  );
}
